/**
 * Whether a redacted output still reflects the current redaction state
 * (VE-25870). Downloading a stale output is how a partially-redacted video
 * reached a public records release in VE-25648.
 *
 * Not every reason is about redaction data. `combined*` answers for a merged
 * document whose parts moved on, `batesNumberingBehind` (VE-30105) for a
 * member whose output does not carry its merged document's current numbering,
 * `batesNumberingOrphaned` and `batesNumberingStray` for the opposite — an
 * output carrying numbering that belongs to nothing, because the file left
 * the set or because the set stopped stamping — and `watermarksBehind`
 * (VE-30104) for one whose output does not carry the set's current
 * watermarks. In each case the file has not changed, the set has. They are reasons here because they mean the same thing to a reviewer
 * about to release, and the same action clears them.
 *
 * The verdict itself is trivial — a list of reasons, any of which makes an output
 * stale. What is not trivial is establishing the first reason, that saved
 * redactions exist which the output does not contain, and callers answer that
 * one of two ways:
 *
 * - **Version label**, for anything saved since VE-25870 / VE-26422. Redact File
 *   tells the engine which version to produce and writes that same number onto
 *   the redaction data it consumes, so the two sides can be compared without
 *   either having observed the other. See `readBurnInLabel`.
 * - **Timestamps**, for saves written before that. Did anything land after the
 *   output was created? The folder view and the document editor anchor on the
 *   output itself, which appears only once the engine or the renderer has
 *   finished — minutes after anything the same click wrote — so no tolerance is
 *   needed. The audio/video tab anchors on the burn-in's own selections asset
 *   instead, to catch saves made while a long engine job runs, and pays for that
 *   with the fences described there.
 *
 * Which of several true reasons a reviewer is actually SHOWN is `staleHeadline`
 * below — one ladder, shared by the banner, the download warning, the download
 * review's rows and the documents panel's tooltips, each supplying its own
 * register over it. That decision lives here rather than in any of them because
 * it had been made four times, and two of the four had never heard of the set
 * reasons at all.
 *
 * Any newer save makes the output stale, which is less blunt than it sounds. A
 * save records which series are selected, and only selected series are sent to
 * the blur engine (`selectUdrRedactions` filters on exactly that), so changing
 * the selection changes what comes out. There is no such thing as a save that
 * moves the redaction data without moving the output. Known limitation: select
 * then unselect leaves no net change and still warns — recognising that needs a
 * real diff of the two bodies, which nothing here does.
 *
 * A redaction-count comparison used to sit between the two. It is gone: it could
 * not see a redaction merely moved or resized, and both surfaces that used it now
 * have something exact or something simpler.
 */

/**
 * More than one can apply at once. `unsavedChanges` is kept distinct from
 * `missingLatestEdits` because the copy differs: one says the edits aren't
 * persisted yet, the other that they are saved but not burned in.
 */
export type StaleReason =
  | 'missingLatestEdits'
  | 'unsavedChanges'
  | 'newerVersionInProgress'
  /**
   * a COMBINED document whose parts have moved on: a member was
   * redacted again, or the membership changed, since it was built. Not derived
   * from redaction data like the others — the comparison is between the assets
   * the combine recorded and the files' current newest — but it means the same
   * thing to a reviewer about to release it, so it travels the same banner and
   * the same download gate rather than inventing a second warning.
   */
  | 'combinedMembersChanged'
  /**
   * a COMBINED document that does not contain every member: files
   * nobody had opened were left out of it on purpose.
   */
  | 'combinedIncomplete'
  /**
   * a COMBINED document that could not be CHECKED: the membership
   * read failed, so there is nothing to compare the recorded members against.
   */
  | 'combinedUnverifiable'
  /**
   * a COMBINED document one of whose files has been DELETED from
   * the request since it was built.
   */
  | 'combinedMemberDeleted'
  /**
   * A MEMBER of a merged document whose output does not carry the set's
   * current numbering (VE-30105): the stamp was applied or changed, or the
   * order moved, after this file was last burned.
   *
   * Not derived from redaction data — the file itself has not changed — but it
   * means the same thing to a reviewer about to release it, and the same
   * action fixes it, so it travels the same banner.
   */
  | 'batesNumberingBehind'
  /**
   * A file that is NO LONGER in a merged document, whose pages still carry
   * that set's numbering (VE-30105).
   *
   * The opposite complaint to the one above: not missing numbers, but holding
   * numbers that no longer apply — and which the member that took its place
   * now also carries, so the request contains two documents numbered from the
   * same start. A rebuild returns it to unnumbered, which is the right state
   * for a file in no set.
   */
  | 'batesNumberingOrphaned'
  /**
   * A member of a merged document whose set carries NO numbering, whose
   * output still carries some (VE-31006).
   *
   * The same defect as `batesNumberingOrphaned` — numbers that belong to
   * nothing — reached from the other direction: the file did not leave the
   * set, the set stopped stamping. It needs its own line because the other
   * one's opens by telling the reviewer the file is not in the merged
   * document, which is plainly contradicted by the panel beside it.
   */
  | 'batesNumberingStray'
  /**
   * A member of a merged document whose output does not carry the SET's
   * current watermarks (VE-30104): they were applied or changed after this
   * file was last burned.
   *
   * Like the numbering reasons above, not derived from redaction data — the
   * file itself has not changed, the set's marking has — and the same Redact
   * File fixes it, so it travels the same banner.
   *
   * There is deliberately no `watermarkOrphaned` twin. A former member's
   * leftover NUMBERS collide with whatever took its place in the order; a
   * leftover watermark collides with nothing, so it is not a defect.
   */
  | 'watermarksBehind';

export interface RedactedFileStaleness {
  readonly isStale: boolean;
  readonly reasons: readonly StaleReason[];
  readonly version: number | undefined;
}

/** Minimum shape of a redaction-data asset record needed to compare versions. */
export interface RedactionDataRecord {
  readonly id?: string;
  readonly createdDateTime: string;
  readonly details?: unknown;
}

/**
 * How a redacted output points back at what produced it, as the blur engine
 * writes it onto `redacted-media.details`. Only the selections pointer is used:
 * it dates the burn-in for outputs too old to carry a version label.
 */
export interface RedactionProvenance {
  /** Names the `redaction-user-selections` asset the burn-in consumed. */
  readonly redactionAssetId?: string;
}

const readId = (record: Record<string, unknown>, key: string) => {
  const value = record[key];
  return typeof value === 'string' && value ? value : undefined;
};

export function readRedactionProvenance(details: unknown): RedactionProvenance {
  if (!details || typeof details !== 'object') {
    return {};
  }
  const record = details as Record<string, unknown>;
  return { redactionAssetId: readId(record, 'redactionAssetId') };
}

/**
 * What a `redaction-media-data` asset says about its own relationship to a
 * burn-in. Three states, and the difference between the last two is the whole
 * reason `burnInRequested` is written even when false:
 *
 * - `burnedIn` — sent for burn-in, and names the output version it was sent to
 *   produce. Compare that against the output's own `version`.
 * - `saved` — an ordinary save, never sent. Certainly not in any output.
 * - `unlabelled` — written before this scheme existed, or by a writer that has no
 *   labels at all (documents). Says nothing either way.
 */
export type BurnInLabel =
  | { readonly state: 'burnedIn'; readonly version: number }
  | { readonly state: 'saved' }
  | { readonly state: 'unlabelled' };

export function readBurnInLabel(details: unknown): BurnInLabel {
  if (!details || typeof details !== 'object') {
    return { state: 'unlabelled' };
  }
  const record = details as Record<string, unknown>;
  if (record.burnInRequested === false) {
    return { state: 'saved' };
  }
  if (record.burnInRequested !== true) {
    return { state: 'unlabelled' };
  }
  // Requested but carrying no version: the label write partly failed. Treated as
  // unlabelled so another method answers rather than nothing does.
  return typeof record.expectedRedactedVersion === 'number'
    ? { state: 'burnedIn', version: record.expectedRedactedVersion }
    : { state: 'unlabelled' };
}
/** Newest by timestamp. Unparseable stamps are skipped. */
function pickNewest<T>(
  records: readonly T[] | undefined,
  timestampOf: (record: T) => string | null | undefined
): T | null {
  let best: T | null = null;
  let bestMs = -Infinity;
  for (const record of records ?? []) {
    const recordMs = new Date(timestampOf(record) ?? '').getTime();
    if (Number.isNaN(recordMs) || recordMs <= bestMs) {
      continue;
    }
    best = record;
    bestMs = recordMs;
  }
  return best;
}

const createdDateTimeOf = (record: RedactionDataRecord) =>
  record.createdDateTime;
/** Newest save, by timestamp rather than the array's order. */
export const pickNewestRedactionData = (
  records: readonly RedactionDataRecord[] | undefined
): RedactionDataRecord | null => pickNewest(records, createdDateTimeOf);

/** Newest of anything else timestamped, e.g. a file's output assets. */
export const pickNewestBy = pickNewest;

export interface RedactedFileStalenessInput {
  /** False when nothing has been burned in — there is then nothing to warn on. */
  readonly hasOutput: boolean;
  /**
   * There is saved redaction data the output does not contain. The caller
   * establishes this however its surface can — a version label, or timestamps for
   * outputs too old to carry one — and owns any safety margin on the comparison;
   * see `savedAfterLegacyBurnIn` in the audio/video selector.
   */
  readonly savedAfterBurnIn?: boolean;
  /**
   * The editor has edits that aren't persisted yet. Both editors pass their dirty
   * flag: the document editor's is set only by redaction mutations, the audio/
   * video editor's `tdoIsChanged` also by selecting a series — which belongs
   * there, since selection decides what the blur engine receives. Known
   * limitation: select then unselect leaves no net change and still warns.
   */
  readonly hasUnsavedChanges?: boolean;
  /**
   * Global settings were saved after the output's redaction data was. Those
   * settings feed the blur, so the output no longer reflects them. Simple by
   * design: it fires on a settings save that changed nothing, which is accepted
   * for now.
   */
  readonly settingsChangedAfterBurnIn?: boolean;
  readonly isBurnInRunning?: boolean;
  readonly version?: number;
}

export function computeRedactedFileStaleness({
  hasOutput,
  savedAfterBurnIn = false,
  hasUnsavedChanges = false,
  settingsChangedAfterBurnIn = false,
  isBurnInRunning = false,
  version,
}: RedactedFileStalenessInput): RedactedFileStaleness {
  if (!hasOutput) {
    return { isStale: false, reasons: [], version };
  }

  const reasons: StaleReason[] = [];

  if (hasUnsavedChanges) {
    reasons.push('unsavedChanges');
  }

  if (settingsChangedAfterBurnIn || savedAfterBurnIn) {
    reasons.push('missingLatestEdits');
  }

  if (isBurnInRunning) {
    reasons.push('newerVersionInProgress');
  }

  return { isStale: reasons.length > 0, reasons, version };
}

/**
 * WHAT IS WRONG with an output, as ONE decision.
 *
 * Several reasons apply at once routinely — a member of a merged document can
 * be behind its own edits, behind the set's numbering and behind its marks all
 * at the same time — and every surface that warns about it has to pick which
 * fact to lead with. That pick is a judgement, not a rendering detail, and it
 * was being made separately in the banner, the download warning, the download
 * review's rows and the documents panel's tooltips: four ladders over one
 * vocabulary, two of which had simply never heard of the set reasons and fell
 * through to "does not include latest redaction edits", which is false about a
 * file whose edits are all present and whose page numbers are not.
 *
 * So the ladder lives here and the surfaces supply vocabulary. What differs
 * between them is register — a banner sentence, a modal sentence, a chip —
 * never which problem outranks which.
 *
 * ORDER, and why:
 *
 * - A burn-in in flight comes first: the next move is to wait, not to act.
 * - Unsaved beats saved-but-not-burned: a different action (save, not redact).
 * - The `combined*` reasons beat the rest because they are about what a
 *   rebuild would CONTAIN, where the others only say it is behind.
 * - REDACTION EDITS BEAT THE SET REASONS. Missing edits mean the output's
 *   redactions are wrong; the set reasons mean they are right and the
 *   numbering or the marking around them is stale. One Redact File clears
 *   either, so naming the graver fact loses the reviewer nothing.
 * - Numbering that no longer APPLIES beats numbering that is merely behind:
 *   one is a document carrying somebody else's page range, the other a
 *   document a rebuild will catch up.
 */
export type StaleHeadline =
  | 'newerVersionInProgress'
  | 'unsavedChanges'
  | 'combinedMemberDeleted'
  | 'combinedMembersChanged'
  | 'combinedIncomplete'
  | 'combinedUnverifiable'
  | 'missingLatestEdits'
  | 'batesNumberingOrphaned'
  | 'batesNumberingStray'
  /** Both of the set's concerns at once — ONE sentence, not two warnings. */
  | 'batesAndWatermarksBehind'
  | 'batesNumberingBehind'
  | 'watermarksBehind';

export const staleHeadline = (
  reasons: readonly StaleReason[]
): StaleHeadline | null => {
  if (reasons.includes('newerVersionInProgress')) {
    return 'newerVersionInProgress';
  }
  if (reasons.includes('unsavedChanges')) {
    return 'unsavedChanges';
  }
  // FIRST of the combined reasons: a deleted file outranks a moved one,
  // because rebuilding changes what the document contains rather than
  // bringing it up to date, and that is the thing to say before any other.
  if (reasons.includes('combinedMemberDeleted')) {
    return 'combinedMemberDeleted';
  }
  if (reasons.includes('combinedMembersChanged')) {
    return 'combinedMembersChanged';
  }
  // After the one above: being behind outranks being incomplete, because
  // rebuilding fixes the first and the second survives it.
  if (reasons.includes('combinedIncomplete')) {
    return 'combinedIncomplete';
  }
  // Last of the combined reasons: a definite problem outranks an unanswered
  // question, and this one is only reached when there is no definite problem
  // to report.
  if (reasons.includes('combinedUnverifiable')) {
    return 'combinedUnverifiable';
  }
  if (reasons.includes('missingLatestEdits')) {
    return 'missingLatestEdits';
  }
  // THESE TWO STAY APART, though both mean "numbering that belongs to
  // nothing". The orphan's copy opens by telling the reviewer the file is not
  // in the merged document — which, for a file that never left it and whose
  // set merely stopped stamping, is contradicted by the panel beside it.
  //
  // Either one also absorbs a watermark complaint rather than combining with
  // it: carrying somebody else's page numbers is the surprising fact, and the
  // same Redact File clears both regardless of which one is named.
  if (reasons.includes('batesNumberingOrphaned')) {
    return 'batesNumberingOrphaned';
  }
  if (reasons.includes('batesNumberingStray')) {
    return 'batesNumberingStray';
  }
  const batesBehind = reasons.includes('batesNumberingBehind');
  const watermarksBehind = reasons.includes('watermarksBehind');
  if (batesBehind && watermarksBehind) {
    return 'batesAndWatermarksBehind';
  }
  // LAST, because it is the narrowest claim: the redactions in the output are
  // right and only its page numbers are behind. It still outranks everything
  // that is not a staleness reason at all — a file whose output says "no
  // redactions were needed" is making a statement, where this is a defect with
  // an action attached, and the action is the same Redact File either way.
  if (batesBehind) {
    return 'batesNumberingBehind';
  }
  // LAST, and below the numbering reasons for the same reason they sit below
  // `missingLatestEdits`: the redactions in the output are right and only its
  // marking is behind. Narrower claim, same Redact File.
  return watermarksBehind ? 'watermarksBehind' : null;
};

/**
 * The banner copy for a verdict, or `null` for no banner. An in-progress burn-in
 * suppresses it — that state has its own banner and showing both is confusing.
 */
const BANNER_MESSAGE_ID: Record<StaleHeadline, string | null> = {
  newerVersionInProgress: null,
  unsavedChanges: 'staleRedactedFileUnsavedBanner',
  combinedMemberDeleted: 'mergedDocumentMemberDeletedBanner',
  combinedMembersChanged: 'staleCombinedDocumentBanner',
  combinedIncomplete: 'mergedDocumentIncompleteBanner',
  combinedUnverifiable: 'mergedDocumentUnverifiableBanner',
  missingLatestEdits: 'staleRedactedFileBanner',
  batesNumberingOrphaned: 'staleBatesNumberingOrphanedBanner',
  batesNumberingStray: 'staleBatesNumberingStrayBanner',
  batesAndWatermarksBehind: 'staleBatesAndWatermarksBanner',
  batesNumberingBehind: 'staleBatesNumberingBanner',
  watermarksBehind: 'staleWatermarksBanner',
};

export const staleBannerMessageId = (
  reasons: readonly StaleReason[]
): string | null => {
  const headline = staleHeadline(reasons);
  return headline === null ? null : BANNER_MESSAGE_ID[headline];
};

/**
 * The copy for INTERCEPTING a handover — the Version Warning modal's words,
 * and the merged document row's pill, which asks the same question about the
 * same file.
 *
 * `hasVersion` picks between the pair for the states that name one. The set's
 * reasons and the combined ones name NO version deliberately: a version
 * counts this file's own burns, and in those states the problem is not the
 * file's at all — its redactions are current, and the merged document around
 * them has moved. A number there invites a comparison with nothing.
 */
export const staleWarningMessageId = (
  reasons: readonly StaleReason[],
  hasVersion: boolean
): string => {
  switch (staleHeadline(reasons)) {
    case 'newerVersionInProgress':
      return hasVersion
        ? 'staleVersionWarningInProgress'
        : 'staleVersionWarningInProgressNoVersion';
    case 'unsavedChanges':
      return hasVersion
        ? 'staleVersionWarningUnsaved'
        : 'staleVersionWarningUnsavedNoVersion';
    // One message either way — the version in play is the COMBINED
    // document's, and naming it would invite the reader to compare it with
    // the member versions it is built from, which are unrelated counters.
    case 'combinedMemberDeleted':
      return 'staleVersionWarningCombinedMemberDeleted';
    case 'combinedMembersChanged':
      return 'staleVersionWarningCombined';
    case 'combinedIncomplete':
      return 'staleVersionWarningCombinedIncomplete';
    case 'combinedUnverifiable':
      return 'staleVersionWarningCombinedUnverifiable';
    case 'batesNumberingOrphaned':
      return 'staleVersionWarningBatesOrphaned';
    case 'batesNumberingStray':
      return 'staleVersionWarningBatesStray';
    case 'batesAndWatermarksBehind':
      return 'staleVersionWarningBatesAndWatermarks';
    case 'batesNumberingBehind':
      return 'staleVersionWarningBatesBehind';
    case 'watermarksBehind':
      return 'staleVersionWarningWatermarks';
    // `missingLatestEdits`, and the empty verdict a caller should not have
    // opened this with — both say the output is behind its own redactions.
    default:
      return hasVersion
        ? 'staleVersionWarningStale'
        : 'staleVersionWarningStaleNoVersion';
  }
};

/**
 * The folder view's Redacted pill, which is the tersest surface of the lot.
 *
 * A TOOLTIP ON A 30px PILL, read in passing while deciding what to release —
 * so it opens with the alarm and states the fact, and stops. The panel's
 * "Needs redacting — ..." is longer on purpose: it is tied to the summary bar
 * counting "n need redacting" above it, and the modal's combined copy carries
 * a recommendation because a modal is where someone has stopped to read. Both
 * would be padding here.
 *
 * One map for files AND the merged document: the ladder already separates
 * them, since a file never reaches a `combined*` headline and the merged
 * document never reaches the others.
 *
 * `unsavedChanges` and `newerVersionInProgress` are unmapped because this
 * surface cannot show them — there is no unsaved work outside an editor, and
 * a burn in flight replaces the pill with "Redacting" before this is reached.
 */
const PILL_TIP_MESSAGE_ID: Partial<Record<StaleHeadline, string>> = {
  missingLatestEdits: 'stalePillTipEdits',
  batesNumberingBehind: 'stalePillTipBates',
  watermarksBehind: 'stalePillTipWatermarks',
  batesAndWatermarksBehind: 'stalePillTipBatesAndWatermarks',
  batesNumberingOrphaned: 'stalePillTipBatesOrphaned',
  batesNumberingStray: 'stalePillTipBatesStray',
  combinedMembersChanged: 'stalePillTipMergedBehind',
  combinedIncomplete: 'stalePillTipMergedIncomplete',
  combinedUnverifiable: 'stalePillTipMergedUnverifiable',
  combinedMemberDeleted: 'stalePillTipMergedMemberDeleted',
};

export const stalePillTipMessageId = (
  reasons: readonly StaleReason[]
): string => {
  const headline = staleHeadline(reasons);
  return (
    (headline === null ? undefined : PILL_TIP_MESSAGE_ID[headline]) ??
    'stalePillTipEdits'
  );
};

/**
 * WHY a file needs redacting again, where the verdict leads and the cause
 * follows: "Needs redacting — the redacted file is behind …".
 *
 * Shared by the documents panel's row tooltips and the folder view's warning
 * on the Redacted pill, which are one claim about one file on two surfaces a
 * reviewer flips between.
 *
 * States with no entry fall back to the plain sentence, and they are the ones
 * neither surface can be showing: nothing here judges a merged document as a
 * whole, and a burn in flight has its own treatment on both.
 */
const NEEDS_REDACTING_MESSAGE_ID: Partial<Record<StaleHeadline, string>> = {
  missingLatestEdits: 'documentNeedsRedacting',
  batesNumberingBehind: 'documentNeedsRedactingBates',
  watermarksBehind: 'documentNeedsRedactingWatermarks',
  batesAndWatermarksBehind: 'documentNeedsRedactingBatesAndWatermarks',
  batesNumberingOrphaned: 'documentNeedsRedactingBatesOrphaned',
  batesNumberingStray: 'documentNeedsRedactingBatesStray',
};

/**
 * The plain sentence, and what every unmapped state falls back to. Callers
 * repeat it as their `defaultMessage` literal because the formatjs lint rule
 * will not take a reference — so this is the id, not the words.
 */
const NEEDS_REDACTING_FALLBACK_ID = 'documentNeedsRedacting';

export const needsRedactingMessageId = (
  reasons: readonly StaleReason[]
): string => {
  const headline = staleHeadline(reasons);
  return (
    (headline === null ? undefined : NEEDS_REDACTING_MESSAGE_ID[headline]) ??
    NEEDS_REDACTING_FALLBACK_ID
  );
};

/** Stable object for callers that need a verdict before data loads. */
export const NOT_STALE: RedactedFileStaleness = {
  isStale: false,
  reasons: [],
  version: undefined,
};
